'use client';

import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useCartContext } from '@/app/contexts/CartProvider';
import { useDiscount } from '@/queries/GetOneDiscount';
import type { GetOneDiscount_content_discount } from '@/queries/types/GetOneDiscount';
import type { SavePromotion_cart_savePromotion } from '@/queries/types/SavePromotion';

import { PROMOTION_CODE } from '../_consts/discounts';
import getPromosFromCart from '../_utils/getPromosFromCart';
import { readFromLocalStorage } from '@/utils/localStorage';

export interface DiscountContextValues {
  appliedDiscountCode: string;
  discount: GetOneDiscount_content_discount | null;
  discountIsLoading: boolean;
  discountIsSuccess: boolean;
  hasDiscount: boolean;
  localPromoCode: string;
  promotionCodes: (string | null | undefined)[];
  urlDiscount: GetOneDiscount_content_discount | null;
  urlDiscountIsLoading: boolean;
  urlDiscountIsSuccess: boolean;
  urlPromotionData: SavePromotion_cart_savePromotion | null | undefined;
}

export const INITIAL_DISCOUNT_VALUES: DiscountContextValues = {
  appliedDiscountCode: '',
  discount: null,
  discountIsLoading: false,
  discountIsSuccess: false,
  hasDiscount: false,
  localPromoCode: '',
  promotionCodes: [],
  urlDiscount: null,
  urlDiscountIsLoading: false,
  urlDiscountIsSuccess: false,
  urlPromotionData: null,
};

const DiscountContext = createContext<DiscountContextValues>(
  INITIAL_DISCOUNT_VALUES,
);

export const useDiscountContext = (): DiscountContextValues => {
  return useContext(DiscountContext);
};

export const DiscountProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [localPromoCode, setLocalPromoCode] = useState<string>('');

  // get cart
  const { refreshCart, urlPromotionData } = useCartContext();
  const cart = refreshCart?.cart;

  useEffect(() => {
    const code = readFromLocalStorage(PROMOTION_CODE);
    setLocalPromoCode(code ?? '');
  }, []);

  const { appliedDiscountCode, promotionCodes } = getPromosFromCart(cart);

  // get discount from applied discount code (may be sitewide if active)
  const {
    data: discountData,
    isLoading: discountIsLoading,
    isSuccess: discountIsSuccess,
  } = useDiscount(appliedDiscountCode);

  // get discount from url discount code in local storage (discount code from /url path only)
  const {
    data: urlDiscountData,
    isLoading: urlDiscountIsLoading,
    isSuccess: urlDiscountIsSuccess,
  } = useDiscount(localPromoCode);

  const values = useMemo(() => {
    const discount = discountData?.discount ?? INITIAL_DISCOUNT_VALUES.discount;
    const urlDiscount =
      urlDiscountData?.discount ?? INITIAL_DISCOUNT_VALUES.urlDiscount;
    const hasDiscount = Boolean(appliedDiscountCode) || Boolean(localPromoCode);

    return {
      appliedDiscountCode,
      discount,
      discountIsLoading,
      discountIsSuccess,
      hasDiscount,
      localPromoCode,
      promotionCodes,
      urlDiscount,
      urlDiscountIsLoading,
      urlDiscountIsSuccess,
      urlPromotionData,
    };
  }, [
    appliedDiscountCode,
    discountData?.discount,
    discountIsLoading,
    discountIsSuccess,
    localPromoCode,
    promotionCodes,
    urlDiscountData?.discount,
    urlDiscountIsLoading,
    urlDiscountIsSuccess,
    urlPromotionData,
  ]);

  return (
    <DiscountContext.Provider value={values}>
      {children}
    </DiscountContext.Provider>
  );
};
